import React, {memo, useMemo, useState, useRef, useEffect} from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  Clipboard,
  Dimensions,
  LayoutChangeEvent,
  Animated,
  Easing,
  StyleSheet,
  Linking,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Carousel from 'react-native-reanimated-carousel';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {styles} from './styles';
import {DASHBOARD_BANNERS, DynamicBannerConfig} from './config';
import FontAwesome5 from 'react-native-vector-icons/FontAwesome5';
import useGetNextMasteryTournament from '@/Hooks/MasterySeries/useGetNextMasteryTournament';

const {width: screenWidth} = Dimensions.get('window');

const ShimmerOverlay = () => {
  const translateX = useRef(new Animated.Value(-screenWidth))?.current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(translateX, {
          toValue: screenWidth * 1.5,
          duration: 3500,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
        Animated.delay(500),
      ]),
    ).start();
  }, [translateX]);

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        StyleSheet.absoluteFillObject,
        {
          width: '40%',
          height: '100%',
          opacity: 0.15,
          zIndex: 0,
          transform: [{translateX}, {skewX: '-25deg'}],
        },
      ]}>
      <LinearGradient
        colors={[
          'transparent',
          'rgba(255, 255, 255, 0.02)',
          'rgba(255, 255, 255, 0.6)',
          'rgba(255, 255, 255, 0.02)',
          'transparent',
        ]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={{flex: 1}}
      />
    </Animated.View>
  );
};

const BannerCarousel = () => {
  const {isMobileOrTab} = useMediaQuery();
  const [containerWidth, setContainerWidth] = useState(screenWidth * 0.95);
  const carouselRef = useRef<any>(null);
  const {data: nextTournamentResp} = useGetNextMasteryTournament();
  const nextTournamentId = nextTournamentResp?.tournament?.id;

  const banners = useMemo<DynamicBannerConfig[]>(() => {
    if (!nextTournamentId) return DASHBOARD_BANNERS;
    return DASHBOARD_BANNERS.map(b => {
      if (b.id !== 'masterclass_opening') return b;
      return {
        ...b,
        onPress: () => {
          Linking.openURL(
            `https://learn.circlechess.com/playChess?tournamentId=${nextTournamentId}`,
          ).catch(err => console.error('Failed to open URL:', err));
        },
      };
    });
  }, [nextTournamentId]);

  const onLayout = (event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width > 0) {
      setContainerWidth(event.nativeEvent.layout.width);
    }
  };

  const handleCopyCoupon = (config: DynamicBannerConfig) => {
    if (!config?.couponCode) return;
    Clipboard.setString(config.couponCode);
    config.onCopyCoupon?.();
  };

  const renderTags = (config: DynamicBannerConfig) => {
    const activeTags = isMobileOrTab ? config?.tagsMobile : config?.tags;
    if (!activeTags?.length) return null;

    if (config.variant === 'feature') {
      return (
        <View style={styles.chipsContainer}>
          {activeTags.map((tag, index) => (
            <View key={index} style={styles.chip}>
              <CCText style={styles.chipText}>{tag}</CCText>
            </View>
          ))}
        </View>
      );
    }

    return (
      <View style={styles.tagsContainer}>
        {activeTags.map((tag, index) => (
          <React.Fragment key={index}>
            <CCText
              fontSize={isMobileOrTab ? FontSizes[12] : FontSizes[14]}
              color={CCColors?.White}
              style={styles.tag}>
              {tag}
            </CCText>
            {index < activeTags.length - 1 && (
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors?.White}
                style={styles.tagSeparator}>
                •
              </CCText>
            )}
          </React.Fragment>
        ))}
      </View>
    );
  };

  const renderCoupon = (config: DynamicBannerConfig) => {
    if (!config?.couponCode) return null;
    return (
      <TouchableOpacity
        style={styles.couponContainer}
        onPress={() => handleCopyCoupon(config)}
        activeOpacity={0.8}>
        <Box style={styles.couponContainerInner}>
          <CCText style={styles.couponText}>{config?.couponCode}</CCText>
          <MaterialCommunityIcon
            name="content-copy"
            size={16}
            color={CCColors?.TextColor?.Dark}
          />
        </Box>
      </TouchableOpacity>
    );
  };

  const renderOffer = (config: DynamicBannerConfig) => {
    if (!config?.offerText) return null;
    return (
      <View style={styles.offerContainer}>
        <CCText style={styles.upToText}>UP TO</CCText>
        <CCText style={styles.offText}>
          {config?.offerText?.replace('UP TO ', '')}
        </CCText>
      </View>
    );
  };

  const renderCheckmarks = (config: DynamicBannerConfig) => {
    if (!config?.checkmarks?.length) return null;
    return (
      <View style={styles.checkmarksContainer}>
        {config?.checkmarks?.map((text, i) => (
          <View key={i} style={styles.checkmarkRow}>
            <FontAwesome5 name="check-circle" size={14} color="#2DD4BF" solid />
            <CCText style={styles.checkmarkText}>{text}</CCText>
          </View>
        ))}
      </View>
    );
  };

  const renderChessPiecesBackground = (config: DynamicBannerConfig) => {
    if (config?.variant !== 'feature') return null;
    const scale = isMobileOrTab ? 0.45 : 1;
    return (
      <View style={styles.chessPiecesOverlay} pointerEvents="none">
        <Image
          source={require('@/Assets/ChessPieces/White-Pawn.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 20 * scale,
              left: '5%',
              width: 120 * scale,
              height: 160 * scale,
              transform: [{rotate: '-15deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Bishop.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -40 * scale,
              left: '20%',
              width: 160 * scale,
              height: 220 * scale,
              transform: [{rotate: '25deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Rook.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 30 * scale,
              left: '38%',
              width: 110 * scale,
              height: 160 * scale,
              transform: [{rotate: '-10deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Knight.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: -20 * scale,
              left: '55%',
              width: 150 * scale,
              height: 200 * scale,
              transform: [{rotate: '-20deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Queen.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -15 * scale,
              left: '72%',
              width: 140 * scale,
              height: 200 * scale,
              transform: [{rotate: '15deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-King.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 10 * scale,
              left: '88%',
              width: 140 * scale,
              height: 190 * scale,
              transform: [{rotate: '30deg'}],
            },
          ]}
        />
      </View>
    );
  };

  const renderBannerItem = ({item}: {item: DynamicBannerConfig}) => {
    const config = item;
    const bgStyle = config?.backgroundColor
      ? {backgroundColor: config?.backgroundColor}
      : {};

    if (isMobileOrTab) {
      return (
        <Box style={[styles.mobileContainer, bgStyle]}>
          {config?.variant === 'feature' && <ShimmerOverlay />}
          {renderChessPiecesBackground(config)}

          {/* Mobile Image Layer (Rendered First to Sit in Background) */}
          {config?.mainImageMobile && config?.variant === 'feature' ? (
            <View style={styles.mobileFeatureMainImageContainer}>
              <Image
                source={require('@/Assets/ChessPieces/White-Pawn.svg')}
                style={[
                  styles.overlappingPieceLeft,
                  {transform: [{scale: 0.65}], left: -35, top: -20},
                ]}
              />
              <Image
                source={require('@/Assets/ChessPieces/White-Rook.svg')}
                style={[
                  styles.overlappingPieceRight,
                  {transform: [{scale: 0.65}], right: -35, bottom: -25},
                ]}
              />
              <Image
                source={config?.mainImageMobile}
                style={styles.mobileFeatureMainImage}
              />
            </View>
          ) : config.mainImageMobile ? (
            <Image
              source={config?.mainImageMobile}
              style={styles.mobileGirlImage}
            />
          ) : null}

          <View
            style={[
              styles.mobileLeftContent,
              config?.variant === 'feature' && {maxWidth: '90%', zIndex: 10},
            ]}>
            {config?.variant === 'feature' && config?.logo ? (
              <View
                style={[
                  styles.featureTopRow,
                  {flexWrap: 'nowrap', marginBottom: 4},
                ]}>
                <Image
                  source={config?.logo}
                  style={[styles.featureLogo, {width: 80, height: 16}]}
                  resizeMode="contain"
                />
                {config?.subTitle && (
                  <View
                    style={[
                      styles.subTitleBadge,
                      {
                        paddingHorizontal: 4,
                        paddingVertical: 2,
                        marginLeft: 4,
                        flexShrink: 1,
                      },
                    ]}>
                    <CCText
                      style={[styles.subTitleBadgeText, {fontSize: 8}]}
                      numberOfLines={1}>
                      {config?.subTitle}
                    </CCText>
                  </View>
                )}
              </View>
            ) : (
              (config?.logo || config?.couponCode) && (
                <View
                  style={[styles.logoContainer, styles.mobileLogoContainer]}>
                  {config?.logo && (
                    <Image source={config?.logo} style={styles.logo} />
                  )}
                  {renderCoupon(config)}
                </View>
              )
            )}

            {config?.variant !== 'feature' && config?.subTitle && (
              <CCText style={styles.subTitle}>{config?.subTitle}</CCText>
            )}

            {config.title && (
              <CCText
                style={[
                  styles.title,
                  styles.mobileTitle,
                  config.variant === 'feature' && styles.featureTitle,
                ]}>
                {config.title}
              </CCText>
            )}

            {renderCheckmarks(config)}
            {renderTags(config)}

            {config.variant === 'feature' ? (
              <TouchableOpacity
                style={{
                  marginTop: 6,
                  alignSelf: 'flex-start',
                  backgroundColor: '#FDE68A',
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 100,
                  zIndex: 100,
                }}
                onPress={() => config?.onPress?.()}>
                <CCText
                  style={{color: '#1F2937', fontWeight: '800', fontSize: 13}}>
                  {config?.priceText} - {config?.buttonText}
                </CCText>
              </TouchableOpacity>
            ) : (
              <View style={styles.mobileBottomRow}>
                {config.priceText && (
                  <View style={styles.priceTextContainer}>
                    <CCText style={styles.priceText}>
                      {config?.priceText}
                    </CCText>
                  </View>
                )}
                {config.buttonText && (
                  <CCButton
                    style={{marginTop: 5}}
                    onPress={() => config?.onPress?.()}>
                    <CCText style={styles.btnText}>{config?.buttonText}</CCText>
                  </CCButton>
                )}
              </View>
            )}
          </View>
        </Box>
      );
    }

    // Web / Desktop
    return (
      <Box
        style={[
          styles.container,
          bgStyle,
          config?.variant !== 'feature' && {
            paddingVertical: 0,
            paddingBottom: 0,
          },
        ]}>
        {/* Shimmer Effect overlay in the background */}
        {config.variant === 'feature' && <ShimmerOverlay />}

        {/* Feature banner: decorative chess pieces background */}
        {renderChessPiecesBackground(config)}

        <View
          style={[
            styles.leftContent,
            config?.variant === 'feature'
              ? isMobileOrTab
                ? {maxWidth: '62%', paddingVertical: 10}
                : {maxWidth: '50%'}
              : {paddingVertical: 10},
          ]}>
          {config?.variant === 'feature' && config?.logo && (
            <View
              style={[
                styles.featureTopRow,
                isMobileOrTab && {flexWrap: 'nowrap', marginBottom: 4},
              ]}>
              <Image
                source={config?.logo}
                style={[
                  styles.featureLogo,
                  isMobileOrTab && {width: 80, height: 16},
                ]}
                resizeMode="contain"
              />
              {config?.subTitle && (
                <View
                  style={[
                    styles.subTitleBadge,
                    isMobileOrTab && {
                      paddingHorizontal: 4,
                      paddingVertical: 2,
                      marginLeft: 4,
                      flexShrink: 1,
                    },
                  ]}>
                  <CCText
                    style={[
                      styles.subTitleBadgeText,
                      isMobileOrTab && {fontSize: 8},
                    ]}
                    numberOfLines={1}>
                    {config?.subTitle}
                  </CCText>
                </View>
              )}
            </View>
          )}

          {config?.variant !== 'feature' && config?.subTitle && (
            <CCText style={styles.subTitle}>{config?.subTitle}</CCText>
          )}

          {config.title && (
            <CCText
              fontSize={
                config?.variant === 'feature' ? FontSizes[18] : FontSizes[24]
              }
              style={[
                styles.title,
                config?.variant === 'feature' && styles.featureTitle,
              ]}>
              {config?.title}
            </CCText>
          )}

          {renderCheckmarks(config)}
          {renderTags(config)}

          {config?.variant !== 'feature' && (
            <View style={styles.bottomRow}>
              {config?.priceText && (
                <View style={styles.priceTextContainer}>
                  <CCText style={styles.priceText}>{config?.priceText}</CCText>
                </View>
              )}
              {config?.buttonText && (
                <CCButton onPress={() => config?.onPress?.()}>
                  <CCText style={styles.btnText}>{config?.buttonText}</CCText>
                </CCButton>
              )}
              {renderOffer(config)}
              {renderCoupon(config)}
            </View>
          )}

          {config.variant === 'feature' && isMobileOrTab && (
            <TouchableOpacity
              style={{
                marginTop: 6,
                alignSelf: 'flex-start',
                backgroundColor: '#FDE68A',
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 100,
                zIndex: 100,
              }}
              onPress={() => config?.onPress?.()}>
              <CCText
                style={{color: '#1F2937', fontWeight: '800', fontSize: 13}}>
                {config?.priceText} - {config?.buttonText}
              </CCText>
            </TouchableOpacity>
          )}
        </View>

        {/* Feature banner: right action area with price + CTA */}
        {config?.variant === 'feature' && !isMobileOrTab && (
          <View style={styles.rightActionContainer}>
            {config?.priceText && (
              <View style={styles.featurePriceBlock}>
                <CCText style={styles.featurePriceAmount}>
                  {config?.priceText}
                </CCText>
              </View>
            )}

            {config?.buttonText && (
              <TouchableOpacity
                style={styles.featureEnrollBtn}
                onPress={() => config?.onPress?.()}
                activeOpacity={0.8}>
                <CCText style={styles.featureEnrollBtnText}>
                  {config.buttonText}
                </CCText>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Promotional: logo overlay */}
        {config?.variant !== 'feature' && config?.logo && (
          <View
            style={{
              position: 'absolute',
              top: 20,
              left: '50%',
              transform: [{translateX: 50}],
            }}>
            <Image source={config.logo} style={styles.logo} />
          </View>
        )}

        {/* Main image / Feature image with overlapping chess pieces */}
        {config?.mainImage && config.variant === 'feature' ? (
          <View
            style={
              isMobileOrTab
                ? styles.mobileFeatureMainImageContainer
                : styles.featureMainImageContainer
            }>
            <Image
              source={require('@/Assets/ChessPieces/White-Pawn.svg')}
              style={
                isMobileOrTab
                  ? [
                      styles.overlappingPieceLeft,
                      {transform: [{scale: 0.65}], left: -35, top: -20},
                    ]
                  : styles.overlappingPieceLeft
              }
            />
            <Image
              source={require('@/Assets/ChessPieces/White-Rook.svg')}
              style={
                isMobileOrTab
                  ? [
                      styles.overlappingPieceRight,
                      {transform: [{scale: 0.65}], right: -35, bottom: -25},
                    ]
                  : styles.overlappingPieceRight
              }
            />
            <Image
              source={
                isMobileOrTab && config?.mainImageMobile
                  ? config?.mainImageMobile
                  : config?.mainImage
              }
              style={
                isMobileOrTab
                  ? styles.mobileFeatureMainImage
                  : styles.featureMainImage
              }
            />
          </View>
        ) : config.mainImage ? (
          <Image source={config.mainImage} style={styles.girlImage} />
        ) : null}
      </Box>
    );
  };

  if (!banners || banners?.length === 0) return null;

  const hasMultipleBanners = banners?.length > 1;

  return (
    <Box style={styles.wrapper} onLayout={onLayout}>
      <Carousel
        ref={carouselRef}
        loop={hasMultipleBanners}
        width={containerWidth}
        height={isMobileOrTab ? 200 : 220}
        autoPlay={hasMultipleBanners}
        autoPlayInterval={10000}
        data={banners}
        renderItem={renderBannerItem}
        scrollAnimationDuration={1000}
        enabled={false}
      />
      {hasMultipleBanners && (
        <>
          <TouchableOpacity
            style={styles.leftArrow}
            onPress={() => carouselRef?.current?.prev()}>
            <FontAwesome5
              name="chevron-left"
              size={isMobileOrTab ? 16 : 24}
              color="#FFF"
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.rightArrow}
            onPress={() => carouselRef?.current?.next()}>
            <FontAwesome5
              name="chevron-right"
              size={isMobileOrTab ? 16 : 24}
              color="#FFF"
            />
          </TouchableOpacity>
        </>
      )}
    </Box>
  );
};

export default memo(BannerCarousel);
